import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["name", "id", "placeholder", "label", "errorText", "required", "error", "expanded", "onChange", "onBlur", "disabled", "appearance", "dataHook", "maxWidth", "maxHeight", "rows", "cols", "noResize", "defaultValue", "tokens"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { forwardRef, useRef, useImperativeHandle } from 'react';
import { transparentize } from 'polished';
import { AppearanceProvider, useAppearance } from '@segment/matcha/foundations/AppearanceContext';
import { ErrorSlot } from '@segment/matcha/components/Input';
import { useFieldset } from '@segment/matcha/components/Fieldset/FieldsetContext';
import { withDesignTokens } from '@segment/matcha/useDesignTokens';
import * as views from './TextArea.views';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
var BaseTextArea = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var name = _ref.name,
      id = _ref.id,
      placeholder = _ref.placeholder,
      label = _ref.label,
      errorText = _ref.errorText,
      required = _ref.required,
      error = _ref.error,
      expanded = _ref.expanded,
      _onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      disabled = _ref.disabled,
      appearance = _ref.appearance,
      dataHook = _ref.dataHook,
      maxWidth = _ref.maxWidth,
      maxHeight = _ref.maxHeight,
      rows = _ref.rows,
      cols = _ref.cols,
      noResize = _ref.noResize,
      defaultValue = _ref.defaultValue,
      tokens = _ref.tokens,
      rest = _objectWithoutProperties(_ref, _excluded);

  var fieldRef = useRef(null);
  var textAreaAppearance = useAppearance(appearance);
  var fieldsetContext = useFieldset();
  var isDisabled = disabled || (fieldsetContext === null || fieldsetContext === void 0 ? void 0 : fieldsetContext.disabledFieldset);
  useImperativeHandle(ref, function () {
    return fieldRef.current;
  });
  return _jsx(AppearanceProvider, {
    appearance: textAreaAppearance,
    children: _jsxs(views.Wrapper, _objectSpread(_objectSpread({
      disabled: isDisabled,
      expanded: expanded
    }, tokens.wrapper), {}, {
      children: [label && _jsx(views.Label, {
        htmlFor: id,
        variant: "formLabel",
        tag: "label",
        tokens: tokens.label,
        children: label
      }), _jsx(views.FieldWrapper, {
        children: _jsx(views.Field, _objectSpread(_objectSpread({
          name: name,
          id: id,
          placeholder: placeholder,
          required: required,
          disabled: isDisabled,
          onChange: function onChange(event) {
            _onChange === null || _onChange === void 0 ? void 0 : _onChange(event, event.target.value);
          },
          error: error,
          ref: fieldRef,
          "aria-invalid": error,
          "aria-describedby": "".concat(name, "-message"),
          onBlur: onBlur,
          "data-hook": dataHook,
          maxWidth: maxWidth,
          maxHeight: maxHeight,
          rows: rows,
          cols: cols,
          defaultValue: defaultValue,
          noResize: noResize
        }, rest), tokens.field))
      }), _jsx(ErrorSlot, {
        inputName: name,
        errorText: errorText
      })]
    }))
  });
});
export var TextArea = withDesignTokens(BaseTextArea, function () {
  return {};
}, {
  light: function light(theme, props) {
    return {
      wrapper: {
        disabledOpacity: 0.75
      },
      label: {
        color: theme.palette.brand.darkAcai[500]
      },
      field: _objectSpread({
        color: theme.palette.brand.darkAcai[700],
        backgroundColor: theme.palette.neutrals.white[100],
        borderColor: theme.palette.brand.darkAcai[300],
        shadowColor: theme.palette.brand.darkAcai[300],
        placeholderColor: theme.palette.brand.darkAcai[500],
        placeholderOpacity: 0.7,
        hoverBackgroundColor: theme.palette.brand.darkAcai[100],
        focusBorderColor: theme.palette.brand.darkAcai[600],
        focusShadowColor: theme.palette.brand.darkAcai[600],
        disabledBorderColor: theme.palette.brand.darkAcai[300]
      }, props.error && {
        borderColor: theme.palette.status.error[200],
        shadowColor: theme.palette.status.error[200],
        hoverBorderColor: theme.palette.status.error[200],
        hoverShadowColor: theme.palette.status.error[200],
        focusBorderColor: theme.palette.status.error[200],
        focusShadowColor: theme.palette.status.error[200]
      })
    };
  },
  dark: function dark(theme, props) {
    return {
      wrapper: {
        disabledOpacity: 0.75
      },
      label: {
        color: theme.palette.neutrals.white[100]
      },
      field: _objectSpread({
        color: theme.palette.neutrals.white[100],
        backgroundColor: theme.palette.brand.darkAcai[600],
        borderColor: theme.palette.brand.darkAcai[600],
        shadowColor: theme.palette.brand.darkAcai[600],
        placeholderColor: theme.palette.brand.darkAcai[200],
        placeholderOpacity: 0.7,
        hoverBorderColor: transparentize(0.9, theme.palette.neutrals.white[100]),
        hoverShadowColor: transparentize(0.9, theme.palette.neutrals.white[100]),
        focusBorderColor: theme.palette.brand.darkAcai[300],
        focusShadowColor: theme.palette.brand.darkAcai[300],
        disabledBorderColor: theme.palette.brand.darkAcai[600]
      }, props.error && {
        borderColor: theme.palette.status.error[100],
        shadowColor: theme.palette.status.error[100],
        hoverBorderColor: theme.palette.status.error[100],
        hoverShadowColor: theme.palette.status.error[100],
        focusBorderColor: theme.palette.status.error[100],
        focusShadowColor: theme.palette.status.error[100]
      })
    };
  },
  twilioLight: function twilioLight(theme, props) {
    return {
      wrapper: {
        disabledOpacity: 0.5
      },
      label: {
        color: theme.palette.twilio.gray[80]
      },
      field: _objectSpread({
        color: theme.palette.twilio.gray[100],
        backgroundColor: theme.palette.neutrals.white[100],
        borderColor: theme.palette.twilio.gray[30],
        placeholderColor: theme.palette.twilio.gray[60],
        placeholderOpacity: 1,
        hoverBorderColor: theme.palette.twilio.gray[60],
        focusBorderColor: theme.palette.twilio.gray[60],
        focusShadowColor: theme.palette.twilio.gray[60],
        disabledBorderColor: theme.palette.twilio.gray[30]
      }, props.error && {
        borderColor: theme.palette.status.error[200],
        shadowColor: theme.palette.status.error[200],
        hoverBorderColor: theme.palette.status.error[200],
        hoverShadowColor: theme.palette.status.error[200],
        focusBorderColor: theme.palette.status.error[200],
        focusShadowColor: theme.palette.status.error[200]
      })
    };
  }
});